import { IExcelReportData, ITableData } from 'types/excelExport';
import {
    createBlankRow,
    createDataRow,
    createHeaderRow,
    createLabelValueRow,
    formatDate,
    formatMonths,
    formatNumber,
    parseNumber,
} from '../helpers';
import { EXCEL_COLORS } from '../constants';
import { LD_EXCEL_SHEET_NAMES, LD_LABELS } from './constants';
import { formatLdSalesStart } from './salesStartDisplay';
import { ILdSummaryAssumptionsSheetParams } from './types';

const formatAddress = (proForma: ILdSummaryAssumptionsSheetParams['proForma']): string => {
    const parts = [
        proForma.address,
        proForma.city,
        proForma.state,
        proForma.zipCode,
    ].filter(Boolean);
    return parts.join(', ');
};

const formatLotWidthRange = (
    residentialLotMixes: ILdSummaryAssumptionsSheetParams['residentialLotMixes'],
): string => {
    const mixes = residentialLotMixes?.residentialLotMixes ?? [];
    if (!mixes.length) return '';
    const widths = mixes.map(m => m.lotWidth);
    const min = Math.min(...widths);
    const max = Math.max(...widths);
    if (min === max) return `${formatNumber(min)} ft`;
    return `${formatNumber(min)} - ${formatNumber(max)} ft`;
};

export const formatLdSummaryAssumptionsSheet = ({
    proForma,
    residentialLotMixes,
    disposition,
    salesSchedule,
    usesSummary,
}: ILdSummaryAssumptionsSheetParams): IExcelReportData => {
    const labels = LD_LABELS.summary;
    const dealName = proForma.dealName || proForma.name || 'Pro Forma';
    const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const siteAcreage = parseNumber(proForma.siteAcreage);
    const holdPeriod = salesSchedule?.holdPeriod ?? proForma.holdPeriod;
    const selloutDate = disposition?.proFormaSaleDate ?? proForma.saleDate;

    const topHeaderRows: ITableData[] = [
        createDataRow([{ value: dealName, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: LD_EXCEL_SHEET_NAMES.SUMMARY_ASSUMPTIONS, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: today, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createBlankRow(2),
    ];

    const projectInfoRows: ITableData[] = [
        createHeaderRow([labels.projectInfo, ''], EXCEL_COLORS.BLUE_HEADER),
        createLabelValueRow(labels.address, formatAddress(proForma), true),
        createLabelValueRow(
            labels.siteAcreage,
            siteAcreage != null ? `${formatNumber(siteAcreage, 2)} acres` : '',
            true,
        ),
        createLabelValueRow(labels.totalLots, proForma.totalLots, true),
        createLabelValueRow(labels.lotWidthRange, formatLotWidthRange(residentialLotMixes), true),
        createBlankRow(2),
    ];

    const keyDatesRows: ITableData[] = [
        createHeaderRow([labels.keyDates, ''], EXCEL_COLORS.BLUE_HEADER),
        createLabelValueRow(labels.analysisStart, formatDate(proForma.startDate, true), true),
        createLabelValueRow(labels.constructionStart, formatDate(proForma.constructionStart, true), true),
        createLabelValueRow(labels.constructionDuration, formatMonths(proForma.constructionDuration), true),
        createLabelValueRow(
            labels.salesStart,
            formatLdSalesStart(proForma, disposition, usesSummary?.developmentEndDate, true),
            true,
        ),
        createLabelValueRow(labels.selloutDate, formatDate(selloutDate, true), true),
        createLabelValueRow(labels.holdPeriod, formatMonths(holdPeriod), true),
    ];

    return {
        topHeaderRows,
        reportTables: [
            { tableHeaderRows: [], tableData: projectInfoRows },
            { tableHeaderRows: [], tableData: keyDatesRows },
        ],
    };
};
